<template>
  <div class="guest-edit-container">
    <div class="page-header">
      <div class="back-button">
        <el-button type="default" icon="el-icon-arrow-left" @click="goBack">返回列表</el-button>
      </div>
      <div class="page-title">
        <h2>{{ isEdit ? '编辑嘉宾信息' : '新增嘉宾' }}</h2>
      </div>
    </div>

    <div class="content-wrapper" v-loading="loading">
      <el-card class="form-card">
        <el-form :model="guestForm" :rules="rules" ref="guestForm" label-width="120px" class="guest-edit-form" size="small">
          <el-divider content-position="left">
            <span class="divider-title">基本信息</span>
          </el-divider>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="8">
              <div class="avatar-wrapper">
                <div class="avatar-label">嘉宾头像</div>
                <FaImageUpload 
                  v-model="guestForm.avatar" 
                  :maxCount="1"
                />
                <div class="hint">建议上传正方形图片，尺寸不小于200x200px</div>
              </div>
            </el-col>
            
            <el-col :xs="24" :sm="16">
              <el-form-item label="姓名" prop="name">
                <el-input v-model="guestForm.name" placeholder="请输入嘉宾姓名"></el-input>
              </el-form-item>
              
              <el-form-item label="公司/单位" prop="company">
                <el-input v-model="guestForm.company" placeholder="请输入公司或单位名称"></el-input>
              </el-form-item>
              
              <el-form-item label="职务" prop="position">
                <el-input v-model="guestForm.position" placeholder="请输入职务"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">
            <span class="divider-title">联系方式</span>
          </el-divider>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="手机号" prop="phone">
                <el-input v-model="guestForm.phone" placeholder="请输入手机号"></el-input>
              </el-form-item>
            </el-col>
            
            <el-col :xs="24" :sm="12">
              <el-form-item label="邮箱" prop="email">
                <el-input v-model="guestForm.email" placeholder="请输入邮箱"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">
            <span class="divider-title">分组设置</span>
          </el-divider>

          <el-row>
            <el-col :xs="24">
              <el-form-item label="分组" prop="category">
                <el-select 
                  v-model="guestForm.category" 
                  placeholder="请选择嘉宾分组"
                  clearable
                  style="width: 300px;"
                >
                  <el-option
                    v-for="item in categoryOptions"
                    :key="item._id"
                    :label="item.name"
                    :value="item._id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">
            <span class="divider-title">排序设置</span>
          </el-divider>

          <el-row>
            <el-col :xs="24">
              <el-form-item label="排序" prop="sortOrder">
                <el-input-number v-model="guestForm.sortOrder" :min="0" :max="999" style="width: 300px;"></el-input-number>
                <div class="hint">数字越大，排序越靠前</div>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-divider content-position="left">
            <span class="divider-title">个人介绍</span>
          </el-divider>
          
          <el-form-item label="个人简介" prop="bio">
            <el-input type="textarea" v-model="guestForm.bio" placeholder="请输入嘉宾个人简介" :rows="5"></el-input>
          </el-form-item>
          
          <el-form-item class="form-actions">
            <el-button type="primary" @click="submitForm('guestForm')" :loading="submitting">{{ isEdit ? '保存修改' : '创建嘉宾' }}</el-button>
            <el-button @click="resetForm('guestForm')">重置</el-button>
            <el-button @click="goBack">取消</el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script>
import { getGuestDetail, addGuest, editGuest, getGuestCategories } from "@/api/guest";

export default {
  data() {
    return {
      loading: false,
      submitting: false,
      isEdit: false,
      guestId: '',
      categoryOptions: [], // 分组选项
      guestForm: {
        name: '',
        company: '',
        position: '',
        phone: '',
        email: '',
        avatar: '',
        bio: '',
        sortOrder: 0,
        category: '' // 分类ID
      },
      rules: {
        name: [{ required: true, message: '请输入嘉宾姓名', trigger: 'blur' }],
        phone: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
    };
  },
  created() {
    this.guestId = this.$route.query.guestId;
    this.isEdit = !!this.guestId;
    
    // 获取分类列表
    this.fetchCategories();
    
    if (this.isEdit) {
      this.fetchGuestDetails();
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: "guest" });
    },
    async fetchCategories() {
      try {
        const response = await getGuestCategories();
        if (response.status === '10000') {
          this.categoryOptions = response.data.categories || [];
        } else {
          this.$message.warning('获取分组列表失败');
        }
      } catch (error) {
        console.error('获取分组列表失败:', error);
      }
    },
    async fetchGuestDetails() {
      this.loading = true;
      try {
        const response = await getGuestDetail(this.guestId);
        if (response.status === '10000') {
          // 将返回的数据映射到表单
          const data = response.data;
          this.guestForm = {
            name: data.name || '',
            company: data.company || '',
            position: data.position || '',
            phone: data.phone || '',
            email: data.email || '',
            avatar: data.avatar || '',
            bio: data.bio || '',
            sortOrder: data.sortOrder || 0,
            category: data.category || ''
          };
        } else {
          this.$message.error(response.message || '获取嘉宾信息失败');
        }
      } catch (error) {
        this.$message.error('获取嘉宾信息失败');
      } finally {
        this.loading = false;
      }
    },
    submitForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          this.submitting = true;
          try {
            let response;
            
            // 准备提交的数据，处理空字段
            const submitData = {
              ...this.guestForm,
              tags: [], // 确保提交空的tags数组
              // 将空字符串分组ID转换为null
              category: this.guestForm.category || null
            };
            
            if (this.isEdit) {
              // 编辑现有嘉宾
              const data = {
                id: this.guestId,
                ...submitData
              };
              response = await editGuest(data);
            } else {
              // 新增嘉宾
              response = await addGuest(submitData);
            }
            
            if (response.status === '10000') {
              this.$message.success({
                message: this.isEdit ? '嘉宾信息已更新' : '嘉宾已添加',
                duration: 2000,
                onClose: () => {
                  this.goBack();
                }
              });
            } else {
              this.$message.error(response.message || (this.isEdit ? '更新失败' : '添加失败'));
            }
          } catch (error) {
            this.$message.error(this.isEdit ? '更新嘉宾信息失败' : '添加嘉宾失败');
          } finally {
            this.submitting = false;
          }
        } else {
          this.$message.error('请检查表单输入');
          return false;
        }
      });
    },
    resetForm(formName) {
      this.$refs[formName].resetFields();
    },
  },
};
</script>

<style scoped>
.guest-edit-container {
  background-color: #f5f7fa;
  min-height: 100%;
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.back-button {
  margin-right: 20px;
}

.page-title h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #303133;
}

.content-wrapper {
  margin-top: 20px;
}

.form-card {
  margin-bottom: 20px;
}

.avatar-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.avatar-label {
  margin-bottom: 10px;
  font-weight: 500;
}

.divider-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.hint {
  color: #909399;
  font-size: 12px;
  margin-top: 5px;
}

.form-actions {
  margin-top: 30px;
  text-align: center;
}

.el-divider {
  margin: 24px 0;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .back-button {
    margin-bottom: 15px;
  }
  
  .avatar-wrapper {
    margin-bottom: 30px;
  }
}
</style> 